import { FC, useMemo, useState } from "react"
import { Alert, Box, Button, Chip, Divider, Typography } from "@mui/material"
import { Add, Edit, DeleteOutlined } from "@mui/icons-material"
import { GridColDef } from "@mui/x-data-grid-pro"
import { TooltipAction } from "../common/tooltipAction"
import { DataGrid, Modal } from "@/core/components"
import { useUrlListState } from "@/core/hooks"
import { getApiErrorMessage } from "@/core/utils"
import { Userbot } from "../../interfaces"
import { useRolesQuery, useDeleteRoleMutation } from "../../store"
import { useUserbotWriteAccess } from "../../hooks"
import { ConfirmDialog } from "../common/confirmDialog"
import { ClientIdentity } from "../identity"
import { RoleForm } from "./roleForm"
import { RoleClientsManager } from "./roleClientsManager"
import { ClientPolicy } from "../common/clientPolicy"

const RoleClientsCell: FC<{ clients: Userbot.Client[] | undefined }> = ({
  clients,
}) => {
  if (!clients || clients.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, py: 0.5 }}>
      {clients.map((client) => (
        <Box key={client.id}>
          <Chip
            size="small"
            variant="outlined"
            label={
              <ClientIdentity client={client} compact showTooltip={false} />
            }
          />
          <ClientPolicy client={client} showHelp={false} />
        </Box>
      ))}
    </Box>
  )
}

export const RolesTable: FC = () => {
  const list = useUrlListState()
  const { data, isFetching, isError, error } = useRolesQuery(list.listArgs, {
    pollingInterval: 60000,
  })
  const canWrite = useUserbotWriteAccess()
  const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation()

  const [editing, setEditing] = useState<Userbot.Role | null>(null)
  const [creating, setCreating] = useState(false)
  const [toDelete, setToDelete] = useState<Userbot.Role | null>(null)

  const columns: GridColDef<Userbot.Role>[] = useMemo(
    () => [
      { field: "name", headerName: "Название", flex: 2 },
      {
        field: "description",
        headerName: "Описание",
        flex: 3,
        sortable: false,
      },
      {
        field: "clients",
        headerName: "Клиенты",
        flex: 3,
        sortable: false,
        renderCell: ({ row }) => <RoleClientsCell clients={row.clients} />,
      },
      {
        field: "isActive",
        headerName: "Активна",
        flex: 1,
        sortable: false,
        renderCell: ({ value }) =>
          value ? (
            <Chip size="small" color="success" label="Да" />
          ) : (
            <Chip size="small" label="Нет" />
          ),
      },
      ...(["updatesEnabled", "catchUpEnabled"] as const).map((field) => ({
        field,
        headerName:
          field === "updatesEnabled"
            ? "Обновления (сохранено)"
            : "Catch-up (сохранено)",
        flex: 2,
        sortable: false,
        renderCell: ({ value }: { value?: boolean }) =>
          value === undefined ? (
            "Неизвестно"
          ) : (
            <Chip size="small" label={value ? "Да" : "Нет"} />
          ),
      })),
    ],
    [],
  )

  return (
    <>
      {canWrite && (
        <Box mb={1} display="flex" justifyContent="flex-end">
          <Button startIcon={<Add />} onClick={() => setCreating(true)}>
            Создать роль
          </Button>
        </Box>
      )}
      {isError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {getApiErrorMessage(error)}
        </Alert>
      )}
      <Box height="80vh">
        <DataGrid<Userbot.Role>
          columns={columns}
          rows={data?.items ?? []}
          getRowId={(row) => row.id}
          getRowHeight={() => "auto"}
          loading={isFetching && !data}
          rowCount={data?.total ?? 0}
          filterMode="client"
          {...list.gridProps}
          rowActions={
            canWrite
              ? [
                  (r) => (
                    <TooltipAction
                      label="Редактировать"
                      icon={<Edit />}
                      onClick={() => setEditing(r.row)}
                    />
                  ),
                  (r) => (
                    <TooltipAction
                      label="Удалить"
                      icon={<DeleteOutlined />}
                      onClick={() => setToDelete(r.row)}
                    />
                  ),
                ]
              : undefined
          }
        />
      </Box>

      {creating && (
        <Modal
          disableBackdropClose
          title="Новая роль"
          onClose={() => setCreating(false)}
          element={<RoleForm onDone={() => setCreating(false)} />}
        />
      )}
      {editing && (
        <Modal
          disableBackdropClose
          title={`Роль: ${editing.name}`}
          onClose={() => setEditing(null)}
          element={
            <Box>
              <RoleForm role={editing} />
              <Divider sx={{ my: 2 }} />
              <RoleClientsManager roleId={editing.id} />
            </Box>
          }
        />
      )}
      <ConfirmDialog
        open={!!toDelete}
        title="Удалить роль?"
        description={toDelete?.name}
        loading={isDeleting}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          if (toDelete) {
            await deleteRole({ id: toDelete.id })
            setToDelete(null)
          }
        }}
      />
    </>
  )
}
